import React, { useState, useEffect } from "react";
import { newAxiosClient } from "@shadowcue/ui";
import { astryx } from "@shadowcue/ui";

interface RenameModalProps {
  conversation: { id: number; name: string; description: string | null } | null;
  authToken: string;
  onClose: () => void;
  onSaved: () => void;
}

const axios = newAxiosClient();

const RenameModal = (props: RenameModalProps) => {
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isOpen = props.conversation !== null;

  useEffect(() => {
    setName(props.conversation?.name ?? "");
    setDescription(props.conversation?.description ?? "");
    setError(null);
  }, [props.conversation?.id]);

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (!nextIsOpen) {
      props.onClose();
    }
  };

  const handleSave = async () => {
    if (!props.conversation) return;

    setSaving(true);
    setError(null);
    try {
      await axios.patch(
        `/api/conversations/${props.conversation.id}`,
        { name, description, formatName: false },
        { headers: { AUTHORIZATION: `Bearer ${props.authToken}` } },
      );
      props.onSaved();
      props.onClose();
    } catch (err: any) {
      setError(err.response?.data?.error ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!props.conversation) return null;

  return (
    <astryx.Dialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      purpose="form"
      width={480}
    >
      <astryx.Layout
        header={
          <astryx.DialogHeader
            title="Edit conversation"
            onOpenChange={handleOpenChange}
          />
        }
        content={
          <astryx.LayoutContent>
            <astryx.VStack gap={4}>
              <astryx.TextInput
                label="Name"
                value={name}
                onChange={(value: string) => setName(value)}
              />

              <astryx.TextArea
                label="Description"
                value={description}
                onChange={(value: string) => setDescription(value)}
              />

              {/* Detached: this floats on its own below the fields, so it must
                  not use the default attached variant (square top corners,
                  meant to tuck under an input). */}
              {error && (
                <astryx.FieldStatus type="error" message={error} variant="detached" />
              )}
            </astryx.VStack>
          </astryx.LayoutContent>
        }
        footer={
          <astryx.LayoutFooter hasDivider>
            <astryx.HStack gap={2} justify="end">
              <astryx.Button
                label="Cancel"
                onClick={() => props.onClose()}
                isDisabled={saving}
              />
              <astryx.Button
                label="Save"
                variant="primary"
                onClick={handleSave}
                isLoading={saving}
              />
            </astryx.HStack>
          </astryx.LayoutFooter>
        }
      />
    </astryx.Dialog>
  );
};

export default RenameModal;
